<template>
    <!--
        Remount the page when it is pointed at a different project — or a different record
        within one.

        The slug is part of the key, not just the code: a code is unique per company, not
        globally, so two workspaces can both hold a RACER. Keyed on the code alone, moving
        between them would keep the first one's page mounted with the second one's data.

        The ticket is in the key for the same reason. Ticket.vue loads once, on mount, and
        never watches the route, so going from CBD-771 to CBD-1082 left the first one on
        screen — which made duplicating a ticket look like it had done nothing. That was
        hidden while the child and parent chips were target="_blank" anchors doing full
        page loads; turning them into RouterLinks is what exposed it.

        A document is keyed by its number alone, never its title (spec 008): 512 of
        512-sow-for-racer. Another document remounts the editor; a rename only corrects the
        address, and remounting then would throw away whatever had not been saved.
    -->
    <RouterView :key="`ws${$route.params.wsId ?? ''}-proj${$route.params.projId ?? ''}-${$route.params.slug ?? ''}/${$route.params.code ?? ''}/${$route.params.ref ?? $route.params.id ?? recordNumber($route.params.docRef)}`" />
    <ImageStudio />
    <DevPanel v-if="isDev" />
    <PageLoadFailed />
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import ImageStudio from '@/Components/Studio/ImageStudio.vue'
import DevPanel from '@/Components/DevPanel.vue'
import PageLoadFailed from '@/Components/PageLoadFailed.vue'
import { useAuthStore } from '@/stores/auth.js'
import { start as startPoller } from '@/lib/poller.js'

const auth  = useAuthStore()
const recordNumber = (segment) => String(segment ?? '').match(/^\d+/)?.[0] ?? ''
const isDev = ref(window.__cortexDev?.() ?? false)

// Sync isDev from auth user on every load — handles both true AND false
watch(() => auth.user, (user) => {
    if (user === null || user === undefined) return
    const enabled = !!user.dev_panel_enabled
    window.__devPanelEnabled = enabled
    isDev.value = enabled
}, { immediate: true })

// Real-time toggle from System Health page
function onDevPanelChanged(e) {
    isDev.value = !!e.detail
}
onMounted(() => {
    startPoller()
    window.addEventListener('cortex-dev-panel-changed', onDevPanelChanged)
})
onUnmounted(() => window.removeEventListener('cortex-dev-panel-changed', onDevPanelChanged))
</script>
